import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LegalLinks } from "@/components/LegalLinks";
import { primaryBtnCls } from "@/components/FormFields";

/* Shown once, in place of the dashboard, when the signed-in user has not
   accepted the version of the terms currently in force. It replaces a checkbox
   that sat on the login form: that one blocked every sign-in, because component
   state cannot remember anything, and recorded nothing when it was ticked.

   Consent is asked for after authentication rather than before it, because both
   sign-in routes leave the page. With OAuth and magic links there is no
   identity to attach an acceptance to until the user comes back, so a box
   ticked on the way out is a claim nobody can store. Here the accepting user is
   known, and Continue writes a row naming them, the version, and the time. */
export const TermsGate = () => {
  const { profile, acceptTerms, signOut } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Someone who accepted an earlier version is being asked again because the
  // document changed. That is a different message from a first sign-in, and
  // saying "one thing before you start" to a two-year customer reads as a bug.
  const isUpdate = !!profile?.terms_accepted_at;

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError(true); return; }
    setSaving(true);
    try {
      await acceptTerms();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record your acceptance. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="terms-gate"
        className="bg-white border border-gray-200 rounded-sm p-8 max-w-md w-full"
      >
        <img src="/Complaudax_logo.svg" alt="Complaudax" className="h-8 w-auto mb-6" />
        <h1 className="font-heading text-xl font-semibold text-[#16324F] mb-2">
          {isUpdate ? "Our terms have changed" : "One thing before you start"}
        </h1>
        <p className="text-sm text-ink-muted mb-6">
          {isUpdate
            ? "We have updated the Terms of Service and Privacy Policy since you last agreed to them. Review the current version and accept it to carry on."
            : "Please accept the Terms of Service and Privacy Policy to use your workspace. We ask once, and again only if the documents change."}
        </p>

        <label htmlFor="accept-terms" className="flex items-start gap-2.5 cursor-pointer">
          <input
            id="accept-terms"
            data-testid="accept-terms-checkbox"
            type="checkbox"
            checked={accepted}
            aria-invalid={error || undefined}
            aria-describedby={error ? "accept-terms-error" : undefined}
            onChange={(e) => {
              setAccepted(e.target.checked);
              if (e.target.checked) setError(false);
            }}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-1"
          />
          <span className="text-sm text-ink-muted">
            I agree to the <LegalLinks />.
          </span>
        </label>
        {error && (
          <p
            id="accept-terms-error"
            role="alert"
            data-testid="accept-terms-error"
            className="mt-2 text-xs text-[#C62828]"
          >
            The terms must be accepted before you can continue.
          </p>
        )}

        {/* Submits enabled rather than disabled while unticked, so a click says
            why instead of doing nothing silently, and the reason is announced. */}
        <button
          type="submit"
          data-testid="accept-terms-submit"
          disabled={saving}
          className={`w-full mt-6 font-medium ${primaryBtnCls}`}
        >
          {saving ? "Saving..." : "Accept and continue"}
        </button>

        {/* Declining has to lead somewhere. Without this, the only way out of a
            screen that cannot be dismissed is closing the tab. */}
        <button
          type="button"
          data-testid="terms-gate-signout"
          onClick={signOut}
          className="w-full mt-3 py-1 text-xs text-ink-muted underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm"
        >
          Sign out instead
        </button>
      </form>
    </main>
  );
};
